import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import { supabase } from "../api/supabase";
import { School } from "../types/school";
import { getSchoolByAdmin } from "../api/SchoolRequests/schoolRequests";
import { useAuth } from "./AuthContext";
import { Student } from "../types/user";
import { deleteStudent, getStudents, updateStudent as updateStudentRequest } from "../api/StudentRequests/studentRequests";
import { track } from "../analytics/posthog";
import { captureException } from "../analytics/sentry";

interface SchoolContextType {
  sales: number;
  attendance: number;
  clients: number;
  school: School | null;
  schoolId: string;
  loading: boolean;
  students: Student[];
  salesChange: number | null;
  attendanceChange: number | null;
  clientsChange: number | null;
  fetchSchool: () => Promise<void>;
  loadStudents: (currentSchoolId?: string, forceRefresh?: boolean) => Promise<void>;
  handleDelete: (id: string) => Promise<void>;
  refreshStudents: () => Promise<void>;
  patchStudent: (id: string, updates: Partial<Student>) => Promise<void>;
  setSchoolId: React.Dispatch<React.SetStateAction<string>>;
  createSchool: (school: Omit<School, "id" | "created_at">) => Promise<void>;
  updateSchool: (id: string, updates: Partial<Omit<School, "id" | "created_at">>) => Promise<void>;
  deleteSchool: (id: string) => Promise<void>;
}

const SchoolContext = createContext<SchoolContextType | undefined>(undefined);

export const SchoolProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [sales, setSales] = useState<number>(0);
  const [attendance, setAttendance] = useState<number>(0);
  const [clients, setClients] = useState<number>(0);
  const [school, setSchool] = useState<School | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [students, setStudents] = useState<Student[]>([]);
  const [schoolId, setSchoolId] = useState<string>("");
  const studentsCacheRef = useRef<Map<string, Student[]>>(new Map());
  const lastStudentsFetchRef = useRef<Map<string, number>>(new Map());
  const [salesChange, setSalesChange] = useState<number | null>(null);
  const [attendanceChange, setAttendanceChange] = useState<number | null>(null);
  const [clientsChange, setClientsChange] = useState<number | null>(null);

  const fetchSchool = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const fetchedSchool = await getSchoolByAdmin(user.id!);
      setSchool(fetchedSchool);
      if (fetchedSchool) {
        setSchoolId(fetchedSchool.id);
      }
    } catch (error) {
      console.error("Error fetching school:", error);
      captureException(error, { feature: "school", action: "fetchSchool" });
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = useCallback(
    async (currentSchoolId?: string, forceRefresh = false) => {
      const targetSchoolId = currentSchoolId || schoolId;
      if (!targetSchoolId) return;

      const now = Date.now();
      const cacheTimeout = 5 * 60 * 1000;
      const lastFetch = lastStudentsFetchRef.current.get(targetSchoolId) || 0;
      const cachedStudents = studentsCacheRef.current.get(targetSchoolId);

      if (!forceRefresh && cachedStudents && now - lastFetch < cacheTimeout) {
        setStudents(cachedStudents);
        return;
      }

      try {
        setLoading(true);
        // getStudents already sorts by last name — no re-sort needed here
        const students = await getStudents(targetSchoolId);

        studentsCacheRef.current.set(targetSchoolId, students);
        lastStudentsFetchRef.current.set(targetSchoolId, now);
        setStudents(students);
      } catch (error) {
        console.error("Error loading students:", error);
        captureException(error, { feature: "school", action: "loadStudents" });
      } finally {
        setLoading(false);
      }
    },
    [schoolId],
  );

  const handleDelete = async (id: string) => {
    try {
      await deleteStudent(id);
      await loadStudents(schoolId, true);
      track("student_deleted");
    } catch (err) {
      captureException(err, { feature: "school", action: "deleteStudent" });
      throw err;
    }
  };

  useEffect(() => {
    if (user) fetchSchool();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const memoizedStudents = useMemo(() => students, [students]);

  const refreshStudents = useCallback(async () => {
    await loadStudents(schoolId, true);
  }, [loadStudents, schoolId]);

  const studentsRef = useRef<Student[]>(students);
  useEffect(() => { studentsRef.current = students; }, [students]);

  const patchStudent = useCallback(async (id: string, updates: Partial<Student>) => {
    const snapshot = studentsRef.current.find((s) => s.id === id);
    const applyPatch = (list: Student[]) =>
      list.map((s) => (s.id === id ? { ...s, ...updates } : s));

    setStudents((prev) => applyPatch(prev));
    if (schoolId) {
      const cached = studentsCacheRef.current.get(schoolId);
      if (cached) studentsCacheRef.current.set(schoolId, applyPatch(cached));
    }

    try {
      await updateStudentRequest(id, updates);
      track("student_updated");
    } catch (err) {
      if (snapshot) {
        const revert = (list: Student[]) =>
          list.map((s) => (s.id === id ? snapshot : s));
        setStudents((prev) => revert(prev));
        if (schoolId) {
          const cached = studentsCacheRef.current.get(schoolId);
          if (cached) studentsCacheRef.current.set(schoolId, revert(cached));
        }
      }
      captureException(err, { feature: "school", action: "patchStudent" });
      throw err;
    }
  }, [schoolId]);

  useEffect(() => {
    if (schoolId) loadStudents(schoolId);
  }, [schoolId, loadStudents]);

  useEffect(() => {
    const fetchMetrics = async () => {
      if (!schoolId) return;

      try {
        setLoading(true);

        const { data: schoolData, error: schoolError } = await supabase
          .from("schools")
          .select("*")
          .eq("id", schoolId)
          .single();

        if (schoolError) throw schoolError;
        setSchool(schoolData);

        const { count: userCount, error: userError } = await supabase
          .from("students")
          .select("*", { count: "exact", head: true })
          .eq("school_id", schoolId);

        if (userError) throw userError;
        setClients(userCount ?? 0);

        const today = new Date().toISOString().split("T")[0];
        const { count: attendanceCount, error: attendanceError } = await supabase
          .from("attendance_records")
          .select("*", { count: "exact", head: true })
          .eq("school_id", schoolId)
          .eq("date", today)
          .eq("status", "present");

        if (attendanceError) throw attendanceError;
        setAttendance(attendanceCount ?? 0);

        const { data: salesData, error: salesError } = await supabase
          .from("sales")
          .select("amount")
          .eq("school_id", schoolId)
          .gte("payment_date", `${today}T00:00:00`)
          .lte("payment_date", `${today}T23:59:59`);

        if (salesError) throw salesError;
        const totalSales = (salesData ?? []).reduce(
          (sum: number, row: { amount: number }) => sum + row.amount,
          0,
        );
        setSales(totalSales);
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yDate = yesterday.toISOString().split("T")[0];

        const { data: yesterdaySales } = await supabase
          .from("sales")
          .select("amount")
          .eq("school_id", schoolId)
          .gte("payment_date", `${yDate}T00:00:00`)
          .lte("payment_date", `${yDate}T23:59:59`);

        const yesterdayTotal = (yesterdaySales ?? []).reduce(
          (sum: number, row: { amount: number }) => sum + row.amount,
          0,
        );
        setSalesChange(
          yesterdayTotal === 0
            ? null
            : Math.round(((totalSales - yesterdayTotal) / yesterdayTotal) * 100),
        );

        const { count: yesterdayAttendance } = await supabase
          .from("attendance_records")
          .select("*", { count: "exact", head: true })
          .eq("school_id", schoolId)
          .eq("date", yDate)
          .eq("status", "present");

        const yAtt = yesterdayAttendance ?? 0;
        setAttendanceChange(
          yAtt === 0 ? null : Math.round((((attendanceCount ?? 0) - yAtt) / yAtt) * 100),
        );
        setClientsChange(null);
      } catch (err) {
        console.error("Error fetching school dashboard metrics:", err);
        captureException(err, { feature: "school", action: "fetchMetrics" });
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [schoolId]);

  const createSchool = async (school: Omit<School, "id" | "created_at">) => {
    if (!user?.id) throw new Error("You must be signed in to create a school.");

    const { data, error } = await supabase
      .from("schools")
      .insert({ ...school, admin_id: user.id })
      .select()
      .single();
    if (error) throw error;

    // Owning the school row (admin_id = you) is what actually grants admin
    // access under RLS. Mirror that onto your own users row too, since
    // create-user and other admin checks key off users.role/school_id.
    const { error: userError } = await supabase
      .from("users")
      .update({ role: "admin", school_id: data.id })
      .eq("id", user.id);
    if (userError) throw userError;

    setSchool(data);
    setSchoolId(data.id);
    track("school_created");
  };

  const updateSchool = async (id: string, updates: Partial<Omit<School, "id" | "created_at">>) => {
    const { data, error } = await supabase
      .from("schools")
      .update(updates)
      .eq("id", id)
      .select()
      .single();
    if (error) throw error;
    setSchool(data);
    track("school_updated");
  };

  const deleteSchool = async (id: string) => {
    const { error } = await supabase.from("schools").delete().eq("id", id);
    if (error) throw error;
    setSchool(null);
    setSchoolId("");
    setStudents([]);
    studentsCacheRef.current.delete(id);
    lastStudentsFetchRef.current.delete(id);
    track("school_deleted");
  };

  return (
    <SchoolContext.Provider
      value={{
        fetchSchool,
        sales,
        students: memoizedStudents,
        attendance,
        clients,
        school,
        loading,
        schoolId,
        salesChange,
        attendanceChange,
        clientsChange,
        loadStudents,
        handleDelete,
        refreshStudents,
        patchStudent,
        setSchoolId,
        createSchool,
        updateSchool,
        deleteSchool,
      }}
    >
      {children}
    </SchoolContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSchool = () => {
  const context = useContext(SchoolContext);
  if (!context) throw new Error("useSchool must be used within a SchoolProvider");
  return context;
};
